/**
 * Normalisation du code NAF/APE au format INSEE officiel `XX.XXY`.
 *
 * Le format officiel INSEE (NAF rév. 2) est : 4 chiffres + 1 lettre majuscule,
 * groupés en `XX.XX` + `Y`. Total : 6 caractères affichés (5 + point).
 *
 * Exemples :
 *   formatCodeNAF("4399E")     → "43.99E"
 *   formatCodeNAF("43.99E")    → "43.99E"
 *   formatCodeNAF("43.99.E")   → "43.99E"
 *   formatCodeNAF("  64-12-z") → "64.12Z"
 *   formatCodeNAF("")          → ""
 *   formatCodeNAF("texte")     → "texte"    // non-parsable : valeur d'origine
 */

import labelsData from './codeNafLabels.json';

const NAF_PATTERN = /^(\d{2})(\d{2})([A-Z])$/;
const SEPARATORS = /[.\-\s_/]/g;

// Cache des libellés (filtre les clés _meta)
const LABELS = Object.fromEntries(
  Object.entries(labelsData).filter(([k]) => !k.startsWith('_'))
);

export function formatCodeNAF(raw) {
  if (raw === null || raw === undefined) return '';
  const s = String(raw).trim();
  if (!s) return '';
  const cleaned = s.replace(SEPARATORS, '').toUpperCase();
  const m = cleaned.match(NAF_PATTERN);
  if (!m) return s; // Non parsable : on retourne tel quel
  return `${m[1]}.${m[2]}${m[3]}`;
}

export function isValidCodeNAF(raw) {
  if (!raw) return false;
  const cleaned = String(raw).replace(SEPARATORS, '').toUpperCase();
  return NAF_PATTERN.test(cleaned);
}

/**
 * Retourne le libellé officiel INSEE pour un code NAF, ou '' si inconnu.
 *   getCodeNAFLabel("43.99C") → "Travaux de maçonnerie générale et gros œuvre de bâtiment"
 *   getCodeNAFLabel("4399C")  → "Travaux de maçonnerie générale et gros œuvre de bâtiment"
 *   getCodeNAFLabel("99.99X") → ""
 */
export function getCodeNAFLabel(raw) {
  const code = formatCodeNAF(raw);
  if (!code || !isValidCodeNAF(code)) return '';
  return LABELS[code] || '';
}

/**
 * Format complet pour affichage : `XX.XXY — Libellé` ou `XX.XXY` si libellé inconnu.
 *   formatCodeNAFWithLabel("4399C")  → "43.99C — Travaux de maçonnerie générale et gros œuvre de bâtiment"
 *   formatCodeNAFWithLabel("99.99X") → "99.99X"
 *   formatCodeNAFWithLabel("")       → ""
 */
export function formatCodeNAFWithLabel(raw, separator = ' — ') {
  const code = formatCodeNAF(raw);
  if (!code) return '';
  const label = getCodeNAFLabel(code);
  return label ? `${code}${separator}${label}` : code;
}

export default formatCodeNAF;
